import type { Locale } from "../../shared/api/types";

/**
 * The words of the What's new page and dialog. Unlike the app chrome (English for everyone, `chromeLocale()`), these show in
 * Thai for everyone, whatever the session locale, as the release notes do (ruling of 18 Oct 2026). The sidebar and
 * account-menu entry that open the page are chrome and stay English. Listed in ALLOWED in test/noThaiLiterals.test.ts.
 * The EN table stays as the record of each string and the fallback for a missing key.
 */
const EN = {
  pageTitle: "What's new",
  pageSub: "Every Giraffe release, newest first",
  dialogTitle: "What's new in Giraffe",
  dialogIntro: "The latest changes in Giraffe:",
  release: "Release {batch}",
  liveOn: "Live on {date}",
  closed: "Feedback closed:",
  allUpdates: "See all updates",
  gotIt: "Got it",
  none: "No releases to show yet.",
};

export type WhatsNewTextKey = keyof typeof EN;

const TEXT: Record<Locale, Record<WhatsNewTextKey, string>> = {
  en: EN,
  th: {
    pageTitle: "มีอะไรใหม่",
    pageSub: "ทุกรุ่นของ Giraffe เรียงจากใหม่สุด",
    dialogTitle: "มีอะไรใหม่ใน Giraffe",
    dialogIntro: "การเปลี่ยนแปลงล่าสุดใน Giraffe:",
    release: "รุ่นที่ {batch}",
    liveOn: "ใช้งานจริงเมื่อ {date}",
    closed: "รายการแจ้งปัญหาที่ปิดแล้ว:",
    allUpdates: "ดูทั้งหมด",
    gotIt: "รับทราบ",
    none: "ยังไม่มีรุ่นให้แสดง",
  },
};

/** The language of the release notes, their page and their dialog: Thai for everyone (ruling of 18 Oct 2026). */
export const RELEASE_NOTES_LOCALE: Locale = "th";

export function whatsNewText(key: WhatsNewTextKey, locale: Locale, values: Record<string, string | number> = {}): string {
  const template = (TEXT[locale] ?? EN)[key];
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in values ? String(values[name]) : match));
}

/** A go-live date in the given language: "17 Oct 2026", or the Thai short month with the Buddhist year. */
export function formatGoLive(isoDate: string, locale: Locale): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
  return new Intl.DateTimeFormat(locale === "th" ? "th-TH" : "en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(date);
}
